import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Paper,
  Grid,
  TextField,
  Container,
  Stack,
  Chip,
  alpha,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DiscountIcon from "@mui/icons-material/LocalOffer";
import NotesIcon from "@mui/icons-material/Notes";
import InfoIcon from "@mui/icons-material/Info";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AsyncSelect from 'react-select/async';
import { searchProducts, addProduct } from "../../product/services/productService";
import { addPurchaseInvoice } from "../services/purchaseService";
import { getSuppliers, searchSuppliersByName, addSupplier } from "../../suppliers/services/supplierService";
import SupplierSelect from "../../suppliers/components/SupplierSelect";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

function AddPurchasePage() {
  const navigate = useNavigate();
  
  // Get VAT rates from Redux store
  const vatRates = useSelector((state) => state.vat?.vatRates || []);
  
  // Product selection state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [newProductData, setNewProductData] = useState({ 
    Name: "", 
    SKU: "", 
    SalePrice: "", 
    PurchasePrice: "" 
  });
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState("");
  const [taxRate, setTaxRate] = useState(21); // Default 21% VAT
  
  // Invoice header state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  
  // Line items
  const [lineItems, setLineItems] = useState([]);
  
  // Auto-generated invoice number
  const [invoiceNumber, setInvoiceNumber] = useState(() => {
    const year = new Date().getFullYear();
    const timestamp = Date.now();
    return `PI-${year}-${String(timestamp % 10000).padStart(4, "0")}`;
  });

  // Flags
  const [updateInventory, setUpdateInventory] = useState(true);
  const [updateCostPrices, setUpdateCostPrices] = useState(true);
  const [isICA, setIsICA] = useState(false); // ICA Purchases flag (critical - default OFF)
  const [isPriceIncludeVAT, setIsPriceIncludeVAT] = useState(true); // Price includes VAT (default ON)

  // Suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  
  // ✅ Dialog states
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', contactPerson: '', email: '', phone: '', address: '' });
  const [supplierFormTouched, setSupplierFormTouched] = useState(false);
  
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [productFormTouched, setProductFormTouched] = useState(false);
  
  // ✅ File upload state
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceFileName, setInvoiceFileName] = useState('');

  // ✅ Auto-calculate totals
  const calculations = React.useMemo(() => {
    const subTotal = lineItems.reduce((sum, item) => sum + item.totalPrice, 0);
    
    // Calculate tax based on whether price includes VAT
    const taxAmount = lineItems.reduce((sum, item) => {
      let itemTax;
      if (isPriceIncludeVAT) {
        // VAT is included - extract it from the price
        itemTax = (item.totalPrice * item.taxRate) / (100 + item.taxRate);
      } else {
        // VAT not included - add it on top
        itemTax = (item.totalPrice * item.taxRate) / 100;
      }
      return sum + itemTax;
    }, 0);
    
    const totalAmount = isPriceIncludeVAT 
      ? subTotal - discountAmount  // VAT already included in subTotal
      : subTotal + taxAmount - discountAmount;  // Add VAT on top
    
    return {
      subTotal: subTotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      discountAmount: Number(discountAmount).toFixed(2),
      totalAmount: totalAmount.toFixed(2)
    };
  }, [lineItems, discountAmount, isPriceIncludeVAT]);

  // Fetch suppliers on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      setSuppliersLoading(true);
      try {
        const response = await getSuppliers();
        const suppliersData = response?.payload || response || [];
        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        toast.error('Failed to load suppliers');
        setSuppliers([]);
      } finally {
        setSuppliersLoading(false);
      }
    };
    fetchSuppliers();
  }, []);

  // Auto-set due date (30 days from invoice date)
  useEffect(() => {
    if (invoiceDate && !dueDate) {
      const invoice = new Date(invoiceDate);
      const due = new Date(invoice);
      due.setDate(due.getDate() + 30);
      setDueDate(due.toISOString().slice(0, 10));
    }
  }, [invoiceDate]);

  const loadProductOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    let paramsObj;
    if (/^\d+$/.test(inputValue)) {
      paramsObj = { id: inputValue, sku: inputValue };
    } else {
      paramsObj = { name: inputValue, sku: inputValue };
    }
    const data = await searchProducts(paramsObj);
    const productsArray = Array.isArray(data?.payload) ? data.payload : Array.isArray(data) ? data : [];
    return productsArray.map((p) => ({
      value: p.Id,
      label: `${p.Name} (ID: ${p.Id})${p.SKU ? " - SKU: " + p.SKU : ""}`,
      ...p,
    }));
  };

  const loadSupplierOptions = async (inputValue) => {
    try {
      if (!inputValue || inputValue.trim().length < 3) return [];
      const response = await searchSuppliersByName(inputValue.trim());
      const searchResults = response?.payload || response || [];
      
      if (Array.isArray(searchResults)) {
        return searchResults.map(s => ({
          value: s.Id,
          label: s.Name,
          Name: s.Name,
          ...s
        }));
      }
      return [];
    } catch (error) {
      console.error('Error searching suppliers:', error);
      toast.error('Failed to search suppliers');
      return [];
    }
  };

  const handleProductSelect = (product) => {
    if (product && product.value === "new") {
      // Open product dialog instead of inline form
      setOpenProductDialog(true);
      setSelectedProduct(null);
    } else {
      setIsNewProduct(false);
      setSelectedProduct(product);
      setUnitPrice(product?.CostPrice || product?.PurchasePrice || "");
    }
  };

  // ✅ Updated handleAddLineItem - merge products if already exists
  const handleAddLineItem = async () => {
    let productId = selectedProduct?.Id;
    let productName = selectedProduct?.Name || newProductData.Name;
    let sku = selectedProduct?.SKU || newProductData.SKU;

    if (isNewProduct) {
      try {
        const response = await addProduct(newProductData);
        const newProd = response?.payload || response;
        productId = newProd.Id;
        productName = newProd.Name;
        sku = newProd.SKU;
      } catch (error) {
        console.error('Error adding new product:', error);
        toast.error('Failed to add new product');
        return;
      }
    }

    if (!productId || !quantity || !unitPrice) {
      toast.error('Please fill all product fields');
      return;
    }

    // ✅ Check if product already exists in line items
    const existingItemIndex = lineItems.findIndex(item => item.productId === productId);

    if (existingItemIndex !== -1) {
      // ✅ Product exists - update quantity and recalculate
      setLineItems(prev => {
        const updated = [...prev];
        const existingItem = updated[existingItemIndex];
        const newQuantity = existingItem.quantity + Number(quantity);
        const newTotalPrice = newQuantity * Number(unitPrice);
        
        updated[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          unitPrice: Number(unitPrice), // Update unit price in case it changed
          taxRate: Number(taxRate), // Update tax rate in case it changed
          totalPrice: newTotalPrice
        };
        
        return updated;
      });
      toast.success(`Quantity updated for ${productName}`);
    } else {
      // ✅ New product - add to line items
      const lineItem = {
        productId,
        productName,
        sku,
        quantity: Number(quantity),
        unitPrice: Number(unitPrice),
        taxRate: Number(taxRate),
        totalPrice: Number(quantity) * Number(unitPrice)
      };

      setLineItems(prev => [...prev, lineItem]);
      toast.success('Line item added');
    }

    // Reset product fields
    setSelectedProduct(null);
    setIsNewProduct(false);
    setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
    setQuantity(1);
    setUnitPrice("");
    setTaxRate(21);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
    toast.info('Line item removed');
  };
  // ✅ Handle Add Supplier
  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      setSupplierFormTouched(true);
      toast.error('Supplier name is required');
      return;
    }

    try {
      const supplierData = {
        Name: newSupplier.name.trim(),
        Contactperson: newSupplier.contactPerson?.trim() || null,
        Email: newSupplier.email?.trim() || null,
        MobileNumber: newSupplier.phone?.trim() || null,
        Street: newSupplier.address?.trim() || null,
        StreetNumber: null,
        PostCode: null,
        City: null,
        Country: null,
        BTWNumber: null,
        Notes: null
      };

      const response = await addSupplier(supplierData);
      toast.success('Supplier added successfully');
      
      // Refresh suppliers list
      const suppliersResponse = await getSuppliers();
      const suppliersData = suppliersResponse?.payload || suppliersResponse || [];
      if (Array.isArray(suppliersData)) {
        setSuppliers(suppliersData);
      }
      
      // Set the newly added supplier as selected
      const addedSupplierId = response.Id || response.id || response.payload?.Id;
      if (addedSupplierId) {
        setSelectedSupplier({
          value: addedSupplierId,
          label: newSupplier.name.trim()
        });
      }
      
      setOpenSupplierDialog(false);
      setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
      setSupplierFormTouched(false);

    } catch (error) {
      console.error('❌ Error adding supplier:', error);
      toast.error(error.response?.data?.message || 'Failed to add supplier');
    }
  };

  // ✅ Handle Add Product
  const handleAddProductFromDialog = async () => {
    if (!newProductData.Name.trim()) {
      setProductFormTouched(true);
      toast.error('Product name is required');
      return;
    }

    try {
      const productData = {
        Name: newProductData.Name.trim(),
        SKU: newProductData.SKU?.trim() || null,
        SalePrice: parseFloat(newProductData.SalePrice) || 0,
        PurchasePrice: parseFloat(newProductData.PurchasePrice) || 0,
        Quantity: 0,
        Description: null,
        CategoryId: null
      };

      const response = await addProduct(productData);
      toast.success('Product added successfully');
      
      // Set the newly added product as selected
      const addedProductId = response.Id || response.id || response.payload?.Id;
      if (addedProductId) {
        setSelectedProduct({
          value: addedProductId,
          label: newProductData.Name.trim(),
          sku: newProductData.SKU?.trim() || ''
        });
        setUnitPrice(newProductData.PurchasePrice || '');
      }
      
      setOpenProductDialog(false);
      setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
      setProductFormTouched(false);

    } catch (error) {
      console.error('❌ Error adding product:', error);
      toast.error(error.response?.data?.message || 'Failed to add product');
    }
  };

  // ✅ Handle File Upload
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (PDF, images)
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a PDF or image file (JPG, PNG)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setInvoiceFile(file);
      setInvoiceFileName(file.name);
      toast.success(`File ${file.name} uploaded successfully`);
    }
  };
  const handleSubmitPurchaseInvoice = async () => {
    // Validation
    if (!selectedSupplier) {
      toast.error('Please select a supplier');
      return;
    }
    
    if (lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    if (!invoiceDate) {
      toast.error('Please select an invoice date');
      return;
    }

    const purchaseData = {
      PurchaseInvoiceNumber: invoiceNumber,
      InvoiceDate: invoiceDate,
      DueDate: dueDate || null,
      SupplierId: selectedSupplier.value,
      Status: "Not Paid", // Default status
      SubTotal: parseFloat(calculations.subTotal),
      TaxAmount: parseFloat(calculations.taxAmount),
      DiscountAmount: parseFloat(calculations.discountAmount),
      TotalAmount: parseFloat(calculations.totalAmount),
      Notes: notes || null,
      ReferenceNumber: referenceNumber || null,
      UpdateInventory: updateInventory,
      UpdateCostPrices: updateCostPrices,
      IsICA: isICA, // ICA Purchases flag
      LineItems: lineItems.map(item => {
        const subtotal = item.quantity * item.unitPrice;
        const discountAmount = (subtotal * (item.discountPercentage || 0)) / 100;
        const subtotalAfterDiscount = subtotal - discountAmount;
        
        // Calculate tax based on whether price includes VAT
        let taxAmount, lineTotal;
        if (isPriceIncludeVAT) {
          // VAT is included in price - extract it
          taxAmount = (subtotalAfterDiscount * (item.taxRate || 0)) / (100 + (item.taxRate || 0));
          lineTotal = subtotalAfterDiscount;
        } else {
          // VAT not included - add it on top
          taxAmount = (subtotalAfterDiscount * (item.taxRate || 0)) / 100;
          lineTotal = subtotalAfterDiscount + taxAmount;
        }
        
        return {
          ProductId: item.productId,
          Quantity: item.quantity,
          UnitCost: item.unitPrice,
          DiscountPercentage: item.discountPercentage || 0,
          DiscountAmount: discountAmount,
          TaxPercentage: item.taxRate || 0,
          TaxAmount: taxAmount,
          LineTotal: lineTotal,
          Description: item.description || null
        };
      })
    };

    try {
      const response = await addPurchaseInvoice(purchaseData);
      toast.success('Purchase invoice created successfully!');
      navigate('/purchases');
    } catch (error) {
      console.error('❌ Error creating purchase invoice:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create purchase invoice');
      }
    }
  };

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="lg">
        {/* Header Card */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
                    <ReceiptIcon sx={{ color: "#1976d2" }} fontSize="large" />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      New Purchase Invoice
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create and manage purchase orders
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={invoiceNumber}
                  color="primary"
                  sx={{ fontWeight: 'bold', fontSize: '1em', borderRadius: '8px', px: 2 }}
                />
              </Box>
            }
            sx={{
              background: alpha("#1976d2", 0.08),
              borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
            }}
          />
          <CardContent>
            <Grid container spacing={3}>
              {/* Supplier Selection */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}`, height: '100%' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonIcon sx={{ color: "#1976d2" }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        Supplier Information *
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenSupplierDialog(true)}
                      sx={{ borderRadius: 1.5, textTransform: 'none' }}
                    >
                      Add New
                    </Button>
                  </Stack>
                  <SupplierSelect
                    value={selectedSupplier}
                    onChange={setSelectedSupplier}
                    loadOptions={loadSupplierOptions}
                    isLoading={suppliersLoading}
                    placeholder="Type at least 3 characters to search..."
                  />
                </Paper>
              </Grid>

              {/* Invoice Details */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}`, height: '100%' }}>
                  <Stack direction="row" alignments="center" spacing={1} mb={2}>
                    <CalendarTodayIcon sx={{ color: "#1976d2" }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      Invoice Details
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Invoice Date *"
                        type="date"
                        value={invoiceDate}
                        onChange={e => setInvoiceDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Due Date"
                        type="date"
                        value={dueDate}
                        onChange={e => setDueDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Reference Number (Supplier's Invoice)"
                        value={referenceNumber}
                        onChange={e => setReferenceNumber(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="e.g., SUP-2024-001"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<UploadFileIcon />}
                          fullWidth
                          sx={{ borderRadius: 1.5, justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                          {invoiceFileName || 'Upload Invoice (PDF/Image)'}
                          <input
                            type="file"
                            hidden
                            accept="application/pdf,image/*"
                            onChange={handleFileUpload}
                          />
                        </Button>
                        {invoiceFileName && (
                          <Chip
                            label={invoiceFileName}
                            onDelete={() => {
                              setInvoiceFile(null);
                              setInvoiceFileName('');
                            }}
                            color="primary"
                            size="small"
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Add Product Card */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InventoryIcon sx={{ color: "#1976d2", mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Add Products
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: isPriceIncludeVAT ? '#4caf50' : '#ff9800',
                      bgcolor: isPriceIncludeVAT ? '#e8f5e9' : '#fff3e0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: isPriceIncludeVAT ? '#2e7d32' : '#f57c00'
                      }}
                    >
                      Price Includes VAT
                    </Typography>
                    <Switch
                      checked={isPriceIncludeVAT}
                      onChange={(e) => setIsPriceIncludeVAT(e.target.checked)}
                      color="success"
                      size="small"
                    />
                  </Paper>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenProductDialog(true)}
                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                  >
                    Add New Product
                  </Button>
                </Box>
              </Box>
            }
            sx={{
              background: alpha("#4caf50", 0.08),
              borderBottom: `1px solid ${alpha("#4caf50", 0.15)}`
            }}
          />
          <CardContent>
            <Grid container spacing={2} alignItems="flex-start">
              {/* ✅ Product Select - Full width on its own row */}
              <Grid item xs={12}>
                <AsyncSelect
                  cacheOptions
                  loadOptions={async (inputValue) => {
                    const options = await loadProductOptions(inputValue);
                    return [...options, { value: "new", label: "+ Add New Product" }];
                  }}
                  value={selectedProduct}
                  onChange={handleProductSelect}
                  placeholder="Search product by name, SKU, or ID..."
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: '12px',
                      minHeight: '45px',
                      borderColor: alpha("#1976d2", 0.3),
                      '&:hover': {
                        borderColor: "#1976d2"
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: '12px',
                      zIndex: 9999
                    })
                  }}
                />
              </Grid>

              {/* ✅ Quantity, Unit Price, Tax Rate, Add Button - All in one row */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Quantity *"
                  type="number"
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, Number(e.target.value)))}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Unit Price *"
                  type="number"
                  value={unitPrice}
                  onChange={e => setUnitPrice(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tax Rate (%)</InputLabel>
                  <Select
                    value={taxRate}
                    onChange={e => setTaxRate(e.target.value)}
                    label="Tax Rate (%)"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, borderRadius: 1.5 }}
                  >
                    {vatRates && vatRates.length > 0 ? (
                      vatRates.map((rate) => (
                        <MenuItem key={rate.Id || rate.id} value={rate.Rate}>
                          {rate.Rate}% {rate.Name ? `- ${rate.Name}` : ''}
                        </MenuItem>
                      ))
                    ) : (
                      [
                        <MenuItem key="0" value={0}>0% - Zero</MenuItem>,
                        <MenuItem key="6" value={6}>6% - Reduced</MenuItem>,
                        <MenuItem key="12" value={12}>12% - Intermediate</MenuItem>,
                        <MenuItem key="21" value={21}>21% - Standard</MenuItem>
                      ]
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  fullWidth
                  onClick={handleAddLineItem}
                  disabled={!selectedProduct || !quantity || !unitPrice}
                  sx={{
                    height: '40px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  Add
                </Button>
              </Grid>
            </Grid>

            {isNewProduct && (
              <Box sx={{ mt: 3, p: 2, bgcolor: alpha("#ff9800", 0.05), borderRadius: 2, border: `1px dashed ${alpha("#ff9800", 0.3)}` }}>
                <Typography variant="subtitle2" fontWeight="bold" mb={2} color="#ff9800">
                  Add New Product
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Product Name *"
                      value={newProductData.Name}
                      onChange={e => setNewProductData({ ...newProductData, Name: e.target.value })}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SKU *"
                      value={newProductData.SKU}
                      onChange={e => setNewProductData({ ...newProductData, SKU: e.target.value })}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Sale Price"
                      type="number"
                      value={newProductData.SalePrice}
                      onChange={e => setNewProductData({ ...newProductData, SalePrice: e.target.value })}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Purchase Price"
                      type="number"
                      value={newProductData.PurchasePrice}
                      onChange={e => setNewProductData({ ...newProductData, PurchasePrice: e.target.value })}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Line Items Table */}
        {lineItems.length > 0 && (
          <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InventoryIcon sx={{ color: "#1976d2", mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      Line Items
                    </Typography>
                    <Chip
                      label={`${lineItems.length} item${lineItems.length > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Box>
              }
              sx={{
                background: alpha("#1976d2", 0.08),
                borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha("#1976d2", 0.05) }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Tax %</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">€{item.unitPrice.toFixed(2)}</TableCell>
                        <TableCell align="right">{item.taxRate}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          €{item.totalPrice.toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveLineItem(idx)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              {/* Totals Section */}
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {/* Notes and Discount */}
                    <Stack spacing={2}>
                      <TextField
                        label="Discount Amount"
                        type="number"
                        value={discountAmount}
                        onChange={e => setDiscountAmount(Math.max(0, Number(e.target.value)))}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><DiscountIcon fontSize="small" /></InputAdornment>,
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                      <TextField
                        label="Notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        placeholder="Add any additional notes..."
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><NotesIcon fontSize="small" /></InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                      
                      {/* ICA Purchases - Critical Setting */}
                      <Paper
                        elevation={3}
                        sx={{
                          p: 2,
                          bgcolor: isICA ? '#fff3e0' : '#f5f5f5',
                          border: isICA ? '2px solid #ff9800' : '2px solid #e0e0e0',
                          borderRadius: 2,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={700} color={isICA ? 'warning.main' : 'text.secondary'}>
                                ⚠️ ICA Purchases
                              </Typography>
                            </Box>
                            <Switch
                              checked={isICA}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const confirmed = window.confirm(
                                    'WARNING: ICA Purchases is a critical setting.\\n\\nEnabling this will affect accounting and tax calculations.\\n\\nAre you sure you want to enable ICA Purchases?'
                                  );
                                  if (confirmed) {
                                    setIsICA(true);
                                  }
                                } else {
                                  setIsICA(false);
                                }
                              }}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#ff9800',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#ff9800',
                                },
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Critical setting - affects accounting & tax calculations
                          </Typography>
                          {isICA && (
                            <Box sx={{ 
                              bgcolor: '#fff3e0', 
                              p: 1, 
                              borderRadius: 1, 
                              border: '1px solid #ff9800',
                              mt: 0.5
                            }}>
                              <Typography variant="caption" color="warning.dark" fontWeight={600}>
                                ⚠️ ICA Purchases mode is ACTIVE
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: alpha("#1976d2", 0.05) }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Subtotal:</Typography>
                          <Typography variant="body1" fontWeight="medium">€{calculations.subTotal}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Tax Amount:</Typography>
                          <Typography variant="body1" fontWeight="medium">€{calculations.taxAmount}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Discount:</Typography>
                          <Typography variant="body1" fontWeight="medium" color="error.main">
                            -€{calculations.discountAmount}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            Total Amount:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            €{calculations.totalAmount}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Settings and Submit */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: alpha("#4caf50", 0.05) }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={updateInventory}
                            onChange={e => setUpdateInventory(e.target.checked)}
                            color="success"
                          />
                        }
                        label="Update Inventory Stock"
                      />
                      <Tooltip title="Automatically update product stock quantities">
                        <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={updateCostPrices}
                            onChange={e => setUpdateCostPrices(e.target.checked)}
                            color="success"
                          />
                        }
                        label="Update Product Cost Prices"
                      />
                      <Tooltip title="Update product cost prices based on this purchase">
                        <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                      </Tooltip>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/purchases')}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      px: 4
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handleSubmitPurchaseInvoice}
                    disabled={!selectedSupplier || lineItems.length === 0}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      px: 4
                    }}
                  >
                    Create Purchase Invoice
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ✅ Supplier Dialog */}
        <Dialog 
          open={openSupplierDialog} 
          onClose={() => {
            setOpenSupplierDialog(false);
            setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
            setSupplierFormTouched(false);
          }} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Add New Supplier</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Supplier Name *"
              fullWidth
              required
              value={newSupplier.name}
              onChange={(e) => {
                setNewSupplier({ ...newSupplier, name: e.target.value });
                if (supplierFormTouched && e.target.value.trim()) {
                  setSupplierFormTouched(false);
                }
              }}
              onBlur={() => {
                if (!newSupplier.name.trim()) {
                  setSupplierFormTouched(true);
                }
              }}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText={supplierFormTouched && !newSupplier.name.trim() ? "Supplier name is required" : "Required field"}
              error={supplierFormTouched && !newSupplier.name.trim()}
            />
            <TextField
              margin="dense"
              label="Contact Person"
              fullWidth
              value={newSupplier.contactPerson || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={newSupplier.email || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
            <TextField
              margin="dense"
              label="Phone"
              fullWidth
              value={newSupplier.phone || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
            <TextField
              margin="dense"
              label="Address"
              fullWidth
              multiline
              rows={2}
              value={newSupplier.address || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => {
                setOpenSupplierDialog(false);
                setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
                setSupplierFormTouched(false);
              }}
              sx={{ borderRadius: 1.5 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddSupplier} 
              variant="contained" 
              color="primary"
              sx={{ borderRadius: 1.5 }}
              disabled={!newSupplier.name.trim()}
            >
              Add Supplier
            </Button>
          </DialogActions>
        </Dialog>

        {/* ✅ Product Dialog */}
        <Dialog 
          open={openProductDialog} 
          onClose={() => {
            setOpenProductDialog(false);
            setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
            setProductFormTouched(false);
          }} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Add New Product</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Product Name *"
              fullWidth
              required
              value={newProductData.Name}
              onChange={(e) => {
                setNewProductData({ ...newProductData, Name: e.target.value });
                if (productFormTouched && e.target.value.trim()) {
                  setProductFormTouched(false);
                }
              }}
              onBlur={() => {
                if (!newProductData.Name.trim()) {
                  setProductFormTouched(true);
                }
              }}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText={productFormTouched && !newProductData.Name.trim() ? "Product name is required" : "Required field"}
              error={productFormTouched && !newProductData.Name.trim()}
            />
            <TextField
              margin="dense"
              label="SKU"
              fullWidth
              value={newProductData.SKU}
              onChange={(e) => setNewProductData({ ...newProductData, SKU: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional - Product code"
            />
            <TextField
              margin="dense"
              label="Purchase Price"
              type="number"
              fullWidth
              value={newProductData.PurchasePrice}
              onChange={(e) => setNewProductData({ ...newProductData, PurchasePrice: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
              helperText="Optional - Cost from supplier"
            />
            <TextField
              margin="dense"
              label="Sale Price"
              type="number"
              fullWidth
              value={newProductData.SalePrice}
              onChange={(e) => setNewProductData({ ...newProductData, SalePrice: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
              helperText="Optional - Selling price"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => {
                setOpenProductDialog(false);
                setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
                setProductFormTouched(false);
              }}
              sx={{ borderRadius: 1.5 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddProductFromDialog} 
              variant="contained" 
              color="primary"
              sx={{ borderRadius: 1.5 }}
              disabled={!newProductData.Name.trim()}
            >
              Add Product
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default AddPurchasePage;